// =============================================
// Profit centers (Location / Unit) for a business entity
// =============================================
// A "profit center" is a business Location or Unit flagged
// `tracks_own_profitability = 1` (see the user_business_location / user_business_unit
// tables, migration 100). Journal lines and bank transactions can be tagged with one
// so per-unit P&L can be reported (migration 101). This is the single place that
// encodes/decodes the picker value and builds the option list, reused by the manual
// Journal Entries form, the bank-txn Post-to-GL flow, and both report tabs.
//
// Encoding: a location option is `loc:<location_id>`, a unit option is `unit:<unit_id>`.
// Choosing a UNIT stores BOTH ids (unit_id + its parent location_id) so reports can
// roll up location→unit; choosing a LOCATION stores location_id with unit_id = null.
// =============================================
import { DropdownOption } from '../../../components/ui/SearchableDropdown';

export interface ProfitCenterRef {
  location_id: number | null;
  unit_id: number | null;
}

export interface ProfitCenterHelper {
  /** Profit centers exist for this entity (business with ≥1 flagged location/unit). */
  hasAny: boolean;
  /** Dropdown options (prepend your own "All"/"None" row as needed). */
  options: DropdownOption[];
  /** Encoded value ("loc:x" | "unit:y" | "") from stored ids. */
  encode: (location_id: number | null | undefined, unit_id: number | null | undefined) => string;
  /** Decode a picker value back to stored ids (unit resolves its parent location too). */
  decode: (value: string) => ProfitCenterRef;
  /** Human label for stored ids (e.g. "Downtown Branch › Kitchen"), or '' if none. */
  label: (location_id: number | null | undefined, unit_id: number | null | undefined) => string;
}

const eq = (a: any, b: any) => String(a) === String(b);

/**
 * Build the profit-center helper for a business entity.
 * @param businessId  user_businesses.business_id of the selected entity (or null when not a business)
 * @param locations   all user_business_location rows
 * @param units       all user_business_unit rows
 */
export function getProfitCenters(
  businessId: number | string | null | undefined,
  locations: Record<string, any>[],
  units: Record<string, any>[],
): ProfitCenterHelper {
  const empty: ProfitCenterHelper = {
    hasAny: false,
    options: [],
    encode: () => '',
    decode: () => ({ location_id: null, unit_id: null }),
    label: () => '',
  };
  if (businessId == null || businessId === '') return empty;

  const bizLocations = (locations || []).filter(l =>
    // eslint-disable-next-line eqeqeq
    l.business_id == businessId && l.is_deleted != 1);
  const bizUnits = (units || []).filter(u =>
    // eslint-disable-next-line eqeqeq
    u.business_id == businessId && u.is_deleted != 1);

  // Fast lookups
  const locById = new Map(bizLocations.map(l => [String(l.location_id), l]));
  const unitById = new Map(bizUnits.map(u => [String(u.unit_id), u]));

  const options: DropdownOption[] = [];
  bizLocations
    .slice()
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    .forEach(loc => {
      const locProfit = loc.tracks_own_profitability === 1;
      const locUnits = bizUnits
        .filter(u => eq(u.location_id, loc.location_id) && u.tracks_own_profitability === 1)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      // Show the location option only if it tracks its own profitability.
      if (locProfit) {
        options.push({ value: `loc:${loc.location_id}`, label: loc.loc_name || `Location ${loc.location_id}`, depth: 0 });
      }
      // A location that itself doesn't track profitability can still HEAD its
      // profitability-tracking units — show it as a non-selectable group header
      // via a disabled-looking option is overkill; instead just indent the units
      // and prefix them with the location name for context.
      locUnits.forEach(u => {
        const locName = loc.loc_name || `Location ${loc.location_id}`;
        options.push({
          value: `unit:${u.unit_id}`,
          label: `${locName} › ${u.unit_name || `Unit ${u.unit_id}`}`,
          depth: locProfit ? 1 : 0,
        });
      });
    });

  const encode: ProfitCenterHelper['encode'] = (location_id, unit_id) => {
    if (unit_id != null && unitById.has(String(unit_id))) return `unit:${unit_id}`;
    if (location_id != null && locById.has(String(location_id))) return `loc:${location_id}`;
    return '';
  };

  const decode: ProfitCenterHelper['decode'] = (value) => {
    if (!value) return { location_id: null, unit_id: null };
    if (value.startsWith('unit:')) {
      const unit_id = Number(value.slice(5));
      const u = unitById.get(String(unit_id));
      return { unit_id, location_id: u ? Number(u.location_id) : null };
    }
    if (value.startsWith('loc:')) {
      return { location_id: Number(value.slice(4)), unit_id: null };
    }
    return { location_id: null, unit_id: null };
  };

  const label: ProfitCenterHelper['label'] = (location_id, unit_id) => {
    if (unit_id != null) {
      const u = unitById.get(String(unit_id));
      if (u) {
        const loc = locById.get(String(u.location_id));
        const locName = loc ? (loc.loc_name || `Location ${loc.location_id}`) : '';
        return `${locName ? locName + ' › ' : ''}${u.unit_name || `Unit ${u.unit_id}`}`;
      }
    }
    if (location_id != null) {
      const loc = locById.get(String(location_id));
      if (loc) return loc.loc_name || `Location ${loc.location_id}`;
    }
    return '';
  };

  return { hasAny: options.length > 0, options, encode, decode, label };
}
